/**
 * Cached client for GET /api/public-config.
 *
 * AuthContext fetches public-config on mount to drive the silent SSO
 * flow; agentService also needs the Entra group mappings to compute the
 * X-User-Groups header. Rather than each call site doing its own fetch,
 * route both through this module-level cache so we hit the backend once
 * per session (TTL 30s) and share the result.
 */

import { getBackendUrl } from '@/config/runtimeConfig';
import type { EntraGroupMapping } from '@/types/admin.types';
import { DEFAULT_ADMIN_CONFIG } from '@/types/admin.types';

export interface PublicConfig {
  features: {
    enableEntraAuth: boolean;
    enableDatabaseAuth: boolean;
  };
  entraGroupMappings: EntraGroupMapping[];
}

export const PUBLIC_CONFIG_FALLBACK: PublicConfig = {
  features: {
    enableEntraAuth:
      DEFAULT_ADMIN_CONFIG.features?.enableEntraAuth ?? false,
    enableDatabaseAuth:
      DEFAULT_ADMIN_CONFIG.features?.enableDatabaseAuth ?? true,
  },
  entraGroupMappings: [],
};

const TTL_MS = 30_000;

let cached: PublicConfig | null = null;
let cachedAt = 0;
let inflight: Promise<PublicConfig> | null = null;

async function fetchOnce(): Promise<PublicConfig> {
  try {
    const r = await fetch(`${getBackendUrl()}/public-config`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json();
    return {
      features: {
        enableEntraAuth:
          data.features?.enableEntraAuth ??
          PUBLIC_CONFIG_FALLBACK.features.enableEntraAuth,
        enableDatabaseAuth:
          data.features?.enableDatabaseAuth ??
          PUBLIC_CONFIG_FALLBACK.features.enableDatabaseAuth,
      },
      entraGroupMappings: Array.isArray(data.entraGroupMappings)
        ? data.entraGroupMappings
        : [],
    };
  } catch (error) {
    console.warn(
      '[publicConfigService] /api/public-config unavailable, using build-time defaults:',
      error,
    );
    return PUBLIC_CONFIG_FALLBACK;
  }
}

/**
 * Return the public config, hitting the backend at most once per
 * `TTL_MS` window. Concurrent callers share a single in-flight promise.
 */
export async function getPublicConfig(): Promise<PublicConfig> {
  const fresh = cached && Date.now() - cachedAt < TTL_MS;
  if (fresh && cached) return cached;
  if (inflight) return inflight;
  inflight = fetchOnce()
    .then((cfg) => {
      cached = cfg;
      cachedAt = Date.now();
      return cfg;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Drop the cache (call on sign-out / role change). */
export function clearPublicConfigCache(): void {
  cached = null;
  cachedAt = 0;
  inflight = null;
}
